import React from 'react';
import { FiPlus, FiTrash2, FiCheck } from 'react-icons/fi';

/**
 * Portal menu for pedagogy search/select (Question Bank).
 */
const PedagogyDropdownMenu = ({
  menuRef,
  position,
  filteredOptions,
  canSaveNew,
  savingNew,
  onSelect,
  onSaveNew,
  deleteMode = false,
  selectedForDelete = [],
  onToggleDeleteSelect,
  onConfirmDelete,
  deletingSelected = false,
  onCancelDeleteMode,
  onToggleDeleteMode,
}) => {
  if (!position) return null;

  const selectedSet = new Set(selectedForDelete);
  const selectedCount = selectedForDelete.length;

  return (
    <div
      ref={menuRef}
      className="assess-mod-pedagogy-menu fixed bg-white border border-slate-100 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col"
      style={{
        left: position.left,
        width: position.width,
        top: position.top,
        bottom: position.bottom,
        zIndex: 99999,
      }}
    >
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-2 shrink-0">
        <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">
          {deleteMode ? 'Select to remove' : 'Pedagogies'}
        </span>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onToggleDeleteMode) onToggleDeleteMode();
          }}
          title={deleteMode ? 'Exit delete mode' : 'Manage / Delete pedagogies'}
          className={`w-6 h-6 rounded-md flex items-center justify-center border transition-all ${
            deleteMode
              ? 'bg-red-600 border-red-600 text-white shadow-sm'
              : 'bg-white border-slate-200 text-slate-400 hover:border-red-200 hover:text-red-600 hover:bg-red-50'
          }`}
        >
          {deleteMode ? <FiCheck className="text-xs" /> : <FiTrash2 className="text-xs" />}
        </button>
      </div>

      <div className="max-h-[240px] overflow-y-auto custom-scrollbar">
        {filteredOptions.map((opt) => {
          const checked = selectedSet.has(opt);
          if (deleteMode) {
            return (
              <label
                key={opt}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-slate-50 ${
                  checked ? 'bg-red-50' : 'hover:bg-slate-50'
                }`}
                onMouseDown={(e) => e.preventDefault()}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleDeleteSelect(opt)}
                  className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500 shrink-0"
                />
                <span className="text-[11px] font-bold text-slate-600">{opt}</span>
              </label>
            );
          }

          return (
            <div
              key={opt}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(opt);
              }}
              className="px-5 py-3 text-[11px] font-bold text-slate-600 hover:bg-red-50 hover:text-[#b91c1c] cursor-pointer transition-all border-b border-slate-50"
            >
              {opt}
            </div>
          );
        })}

        {!deleteMode && canSaveNew && (
          <button
            type="button"
            disabled={savingNew}
            onMouseDown={(e) => {
              e.preventDefault();
              onSaveNew();
            }}
            className="assess-mod-pedagogy-add w-full px-5 py-3 text-left text-[11px] font-black text-[#b91c1c] bg-red-50/80 hover:bg-red-50 cursor-pointer transition-all border-t border-red-100 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FiPlus className="text-sm shrink-0" />
            {savingNew ? 'Saving…' : 'Add / Save New Pedagogy'}
          </button>
        )}

        {filteredOptions.length === 0 && !deleteMode && !canSaveNew && (
          <div className="px-5 py-3 text-[11px] font-bold text-slate-400">No matches</div>
        )}

        {deleteMode && filteredOptions.length === 0 && (
          <div className="px-5 py-3 text-[11px] font-bold text-slate-400">No matches</div>
        )}
      </div>

      {deleteMode && (
        <div className="flex items-center gap-2 p-3 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            disabled={selectedCount === 0 || deletingSelected}
            onMouseDown={(e) => {
              e.preventDefault();
              onConfirmDelete();
            }}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wide bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiTrash2 className="text-xs" />
            {deletingSelected ? 'Deleting…' : `Delete${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
          </button>
          <button
            type="button"
            disabled={deletingSelected}
            onMouseDown={(e) => {
              e.preventDefault();
              onCancelDeleteMode();
            }}
            className="px-3 py-2 rounded-lg text-[10px] font-bold text-slate-600 border border-slate-200 bg-white hover:border-slate-300 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default PedagogyDropdownMenu;
